import { Component, OnInit, OnDestroy, Input, ViewChild, AfterViewChecked } from '@angular/core';
import { ProfileMin } from '../../../../profiles/shared/profile-min.model';
import { UtilService, DialogService } from '../../../../shared/index';
import { EventRead } from '../../../shared/event-read.model';
import { RegistrationUpsert } from '../../shared/crud/registration-upsert.model';
import { AdjustmentType } from '../../../../revenue/shared/adjustment-types/adjustment-type.model';
import { PaymentType } from '../../../../revenue/shared/payment-types/payment-type.model';
import { RegistrationAttendee } from '../../shared/registration-attendee.model';
import { RegistrationAttendeeItem } from '../../shared/registration-attendee-item.model';
import { RegistrationAttendeeItemCustomResponse } from '../../shared/registration-attendee-item-custom-response.model';
import { RegistrationItem } from '../../shared/registration-item.model';
import { RegistrationItemCustomField } from '../../shared/registration-item-custom-field.model';
import { RegistrationItemCustomFieldAvailableValue } from '../../shared/registration-item-custom-field-avail-value.model';
import { RegistrationItemCustomFieldAvailableValueCreditType } from '../../shared/registration-item-custom-field-avail-value-credit-type.model';
import { CreditCardPaymentInfo } from '../../../../revenue/shared/credit-card/credit-card-payment-info.model';
import { CreditCardContactInfo } from '../../../../revenue/shared/credit-card/credit-card-contact-info.model';
import { IValidationComponent } from '../../../../shared/widgets/component/ivalidation.component';
import { ValidationResult } from '../../../../shared/widgets/validators/validation-result.model';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { EventPaymentMethodService } from '../../../shared/payment-methods/event-payment-method.service';
import { EventPaymentMethod } from '../../../shared/payment-methods/event-payment-method.model';
import { CreditCardService } from '../../../../revenue/shared/credit-card/credit-card.service';
import { CreditCardInputComponent } from '../../../../revenue/credit-card-input/credit-card-input.component';
import { EventPromoCode } from '../../../shared/promo-codes/event-promo-code.model';
import { EventPromoCodesService } from '../../../shared/promo-codes/event-promo-codes.service';
import { RegistrationService } from '../../shared/registration.service';
import { PromoCodeDiscountType } from '../../../shared/promo-codes/promo-code-discount-type';
import { GatewayPaymentProfile, isSavedCardSelected } from '../../../../revenue/shared/credit-card/card-on-file/gateway-payment-profile.model';
import { GatewayCustomerProfile } from '../../../../revenue/shared/credit-card/card-on-file/gateway-customer-profile.model';
import { RegistrationRevenue } from '../../shared/registration-revenue.model';
import { CreditCardGatewayService } from '../../../../revenue/shared/credit-card-gateway/credit-card-gateway.service';

import { RegistrationProfileBenefit } from '../../shared/registration-profile-benefit.model';
import { ProfileBenefit } from '../../../../benefits/shared/profile-benefit.model';
import { EventBenefit } from '../../../../benefits/shared/event-benefit.model';
import { BenefitService } from '../../../../benefits/shared/benefit.service';
import { EventPriceBenefit } from '../../../../benefits/shared/event-price-benefit.model';
import { ConfigService } from '../../../../config/shared/config.service';
import { ConfigSetting } from '../../../../config/shared/config-setting.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GatewayResolutionService } from '../../../../revenue/shared/gateway-resolution/gateway-resolution.service';
import { TokenizeGatewayDto } from '../../../../revenue/shared/gateway-resolution/dto/tokenize-gateway.dto';
import { GatewayResolutionDto } from '../../../../revenue/shared/gateway-resolution/dto/gateway-resolution.dto';

@Component({
  selector: 'wl-registration-wizard-payment-info',
  styleUrls: ['registration-wizard-payment-info.component.css'],
  templateUrl: 'registration-wizard-payment-info.component.html',
  providers: [
    EventPaymentMethodService,
    CreditCardService,
    EventPromoCodesService,
    RegistrationService,
    BenefitService,
    CreditCardGatewayService,
  ],
    standalone: false
})
export class RegistrationWizardPaymentInfoComponent implements OnInit, OnDestroy, IValidationComponent, AfterViewChecked {
  @ViewChild('creditCardInputComponent', { static: false }) creditCardInputComponent: CreditCardInputComponent;

  public _utilService: UtilService;
  public _translationService: TranslationService;
  public _paymentMethodService: EventPaymentMethodService;
  public _eventPromoCodesService: EventPromoCodesService;
  public _registrationService: RegistrationService;
  public _creditCardService: CreditCardService;
  public _benefitService: BenefitService;
  public _configService: ConfigService;
  public _creditCardGatewayService: CreditCardGatewayService;

  public promoCodeDiscountType: any = PromoCodeDiscountType;

  public registrationId: number;
  public registration: RegistrationUpsert = new RegistrationUpsert();
  public event: EventRead = new EventRead();
  public initialLoadComplete: boolean = false;

  // The gateway the Preczn card widget tokenizes against, from revenue-item resolution. This is
  // NOT the same question as _primaryGatewayId (events-v4), which is payment-type-keyed and
  // answers whose stored card-on-file profiles to list; the two can legitimately disagree and are
  // deliberately kept apart.
  public resolvedGateway: TokenizeGatewayDto | null = null;
  private _onDestroy$ = new Subject<void>();
  // This screen's own gateway resolution, kept so saved cards are fetched for the same gateway the items resolve to.
  private _resolution: Promise<GatewayResolutionDto | null> | null = null;
  // Bumped on every saved-card fetch so a response for an older resolution is dropped.
  private _cardOnFileSeq = 0;

  public paymentMethods: Array<EventPaymentMethod>;

  public eventPromoCodes: Array<EventPromoCode>;
  public selectedPromoCode: number;
  public originalAmountPaid: number;

  public hasCardOnFile: boolean;
  public hasCreditCardOnFileEnabled: boolean;
  public gatewayCustomerProfile: GatewayCustomerProfile = new GatewayCustomerProfile();
  public selectedGatewayPaymentProfile: GatewayPaymentProfile = new GatewayPaymentProfile();
  public gatewayPaymentProfiles: Array<GatewayPaymentProfile>;

  public selectedCcPaymentProfileId: string = '';
  public readonly isSavedCardSelected = isSavedCardSelected;
  public hasPaymentOptionFees: boolean = false;

  public availableEventBenefits: Array<EventBenefit>;
  public availableProfileBenefits: Array<ProfileBenefit>;
  public availableBenefitsToUse: Array<EventBenefit>;

  public selectedProfileBenefit: ProfileBenefit;
  public selectedEventBenefit: EventBenefit;
  public selectedRegistrationBenefit: RegistrationProfileBenefit;

  public hasBenefitsThatCanBeUsed: boolean;
  public showPromoCodeNotStarted: boolean = false;
  public showPromoCodeExpired: boolean = false;
  public showPromoCodeMaxedOut: boolean = false;

  public constructor(
    utilService: UtilService,
    translationService: TranslationService,
    paymentMethodService: EventPaymentMethodService,
    eventPromoCodesService: EventPromoCodesService,
    registrationService: RegistrationService,
    creditCardService: CreditCardService,
    benefitService: BenefitService,
    configService: ConfigService,
    creditCardGatewayService: CreditCardGatewayService,
    private _gatewayResolutionService: GatewayResolutionService
  ) {
    this._utilService = utilService;
    this._translationService = translationService;
    this._paymentMethodService = paymentMethodService;

    this._eventPromoCodesService = eventPromoCodesService;
    this._registrationService = registrationService;
    this._creditCardService = creditCardService;
    this._configService = configService;
    this._creditCardGatewayService = creditCardGatewayService;

    this._benefitService = benefitService;

    this.paymentMethods = new Array<EventPaymentMethod>();
    this.eventPromoCodes = new Array<EventPromoCode>();
    this.selectedPromoCode = 0;
    this.registrationId = 0;
    this.originalAmountPaid = 0;

    this.hasCardOnFile = false;
    this.hasCreditCardOnFileEnabled = false;
    this.gatewayPaymentProfiles = new Array<GatewayPaymentProfile>();

    this.availableEventBenefits = new Array<EventBenefit>();
    this.availableProfileBenefits = new Array<ProfileBenefit>();
    this.availableBenefitsToUse = new Array<EventBenefit>();

    this.selectedProfileBenefit = new ProfileBenefit();
    this.selectedEventBenefit = new EventBenefit();
    this.selectedRegistrationBenefit = new RegistrationProfileBenefit();
    this.hasBenefitsThatCanBeUsed = false;
  }

  ngOnInit() {
    this._gatewayResolutionService
      .observeTokenizeGatewayForCard()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((gateway) => {
        this.resolvedGateway = gateway;
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  /**
   * Re-resolves the tokenizing gateway from the currently selected revenue items.
   *
   * The host calls this from registrationItemsChanged(), not only at mount: this payment step is
   * instantiated eagerly and merely hidden between wizard steps, so the component instance
   * survives every item change. Resolving once at mount would leave the gateway stale the moment
   * the registrant changed what they were buying.
   */
  public refreshResolvedGateway = (): Promise<void> => {
    const resolution = this.startResolution();
    if (this.hasCreditCardOnFileEnabled) {
      this.loadCardOnFileData(this.registration.Registration.ProfileId);
    }
    return resolution.then(() => {});
  };

  private startResolution(): Promise<GatewayResolutionDto | null> {
    const itemNums = (this.registration?.Items ?? []).filter((item) => item.Quantity > 0).map((item) => item.ItemNum);
    // A failed resolution becomes null, which the credit card service treats as "no specific gateway".
    this._resolution = this._gatewayResolutionService.resolveForRevenueItems(itemNums).catch(() => null);
    return this._resolution;
  }

  ngAfterViewChecked() {
    //see if even though an item may not have changed but now they select a custom field
    //that has a cost see if the total has changed
    if (this.registration.IsEdit) {
      //we changed from OriginalInvoiceTotalAmount to OriginalItemTotalAmount because a manually invoice could so the same amount if a registration has no profiel info then staff manually changes the invoice price to a member price then a staff goes to edit the reg to then apply a member profileid to it.  The
      if (
        this._utilService.round(this.registration.Registration.OriginalItemTotalAmount, 2) !=
        this._utilService.round(this.registration.Registration.TotalAmount, 2)
      ) {
        this.registration.EditTotalAmountChanged = true;
      } else {
        this.registration.EditTotalAmountChanged = false;
      }
    }
  }

  public loadData(): Promise<any> {
    const getEventPaymentMethodPromise = this._paymentMethodService
      .getEventPaymentMethods(this.event.EventId)
      .then((data: Array<EventPaymentMethod>) => {
        this.paymentMethods = data;
        if (this.registration.IsEdit) {
          // add a no payment or select payment option
          const nonePaymentMethod = new EventPaymentMethod();
          nonePaymentMethod.Accept = true;
          nonePaymentMethod.Descr = this._translationService.getInstantTitlecase(
            'events.registration.wizard.payment-info.additional-payment-select-dropdown-label'
          );
          nonePaymentMethod.PaymentMethodId = 0;
          this._utilService.arrayAddEntryToBeginning(this.paymentMethods, nonePaymentMethod);
          //// set none as the default for edit

          // default
          this.registration.Registration.PaymentOptionForEdit = 0;
        }

        this.hasPaymentOptionFees = this.paymentMethods.some(function (x) {
          return x && x.Fee && x.Fee > 0;
        });

        this.updateRegistrationWithSelectedPaymentMethod();
        this._registrationService.calculateTotals(this.registration);
      });

    this._eventPromoCodesService.getEventPromoCodes(this.event.EventId).then((data: Array<EventPromoCode>) => {
      this.eventPromoCodes = data;
    });

    const getRegistrationRevenuePromise = this._registrationService
      .getRegistrationRevenue(this.registrationId)
      .then((data: RegistrationRevenue) => {
        this.registration.Registration.PaymentAmount = data.AdjustedAmountPaid;
        this.registration.Registration.AlreadyPaidAmountForEdit = data.AdjustedAmountPaid;
        // looks at the existing invoice (it may have been manually editied)
        this.registration.Registration.OriginalInvoiceTotalAmount = data.AdjustedInvoicedAmount;
        // looks at what the amount should be based on the original items the reg has
        this.registration.Registration.OriginalItemTotalAmount = this.registration.Registration.TotalAmount;

        // store the original amount paid before any modifications are made
        // this is used so we know if we need to display a payment adjustment type or not
        // if this is 0 we will have no adjustments needed for payments
        this.originalAmountPaid = data.AdjustedAmountPaid;

        this.updateRegistrationWithSelectedPaymentMethod();
        this._registrationService.calculateTotals(this.registration);

        this.getItemTotals();

        if (this.registration.IsEdit) {
          if (this.registration.Registration.DoNotInvoice == true) {
            if (this.registration.ModificationOptions.DoNotModifyInvoice == false) {
              // default this to do not modify
              this.registration.ModificationOptions.DoNotModifyInvoice = true;
            }
          } else {
            this._configService.getConfigSetting('CheckDoNotModifyInvoicesPaymentsByDefault').then((data: ConfigSetting) => {
              if (data.SettingValue?.toLowerCase() === 'true') {
                this.registration.ModificationOptions.DoNotModifyInvoice = true;
              }
            });
          }
        }

        this.initialLoadComplete = true;
      });

    const getRegistrationPromoCodesPromise = this._registrationService
      .getRegistrationPromoCodes(this.registrationId)
      .then((data: Array<any>) => {
        this.registration.PromoCodes = data;

        this.updateRegistrationWithSelectedPaymentMethod();
        this._registrationService.calculateTotals(this.registration);
      });

    // 3 = Credit Card Payment Option
    if (this.registration && this.registration.Registration && this.registration.Registration.PaymentOption == 3) {
      if (isSavedCardSelected(this.selectedGatewayPaymentProfile?.CcPaymentProfileId)) {
        this.copySelectedCardOnFile();
      } else {
        this.copyContactInfoToCCContactInfo();
      }
    }

    this.checkCardOnFileConfig();

    this.checkCardConnect();

    this.refreshBenefits();

    return Promise.all([getEventPaymentMethodPromise, getRegistrationPromoCodesPromise, getRegistrationRevenuePromise]);
  }

  public checkCardConnect = (): void => {
    // can't mix and match card connect gateways with other gateways. if there's a card connect gateway, use it.
    this._creditCardGatewayService.hasCardConnectCreditCardGateways().then((data) => {
      if (data) this.creditCardInputComponent.useCardConnect = true;
    });
  };

  public checkCardOnFileConfig = (): void => {
    this._creditCardService.hasCreditCardOnFileEnabled().then((data: boolean) => {
      //console.error(data);
      this.hasCreditCardOnFileEnabled = data;
    });
  };

  public loadCardOnFileData = (profileId: number): void => {
    const seq = ++this._cardOnFileSeq;
    const resolution = this._resolution ?? this.startResolution();
    resolution
      .then((r) => this._creditCardService.getGatewayCustomerProfileForResolution(profileId, r))
      .then((data: GatewayCustomerProfile) => {
        // a newer resolution or call has superseded this one
        if (seq !== this._cardOnFileSeq) return;

        this.gatewayCustomerProfile = data ?? new GatewayCustomerProfile();
        this.gatewayPaymentProfiles = data?.PaymentProfiles ?? [];

        var _paymentProfile = new GatewayPaymentProfile();
        _paymentProfile.CcPaymentProfileId = '';
        _paymentProfile.AccountType = 0;
        _paymentProfile.ECheckType = 0;
        this._translationService
          .getTitlecase('events.registration.wizard.payment-info.card-on-file-new-card')
          .then((translation) => (_paymentProfile.CardNumber = translation));
        this.gatewayPaymentProfiles.unshift(_paymentProfile);

        this.reconcileSelectedCardOnFile();
      });
  };

  // Keeps a chosen saved card only if the reloaded list still offers it; otherwise falls back to a new card.
  private reconcileSelectedCardOnFile(): void {
    const prevId = this.selectedGatewayPaymentProfile?.CcPaymentProfileId;
    if (!isSavedCardSelected(prevId)) return;

    const match = this.gatewayPaymentProfiles.find((x) => String(x.CcPaymentProfileId) === String(prevId));
    if (match) {
      this.selectedGatewayPaymentProfile = match;
    } else {
      this.selectedCcPaymentProfileId = '';
      this.selectedGatewayPaymentProfile = new GatewayPaymentProfile();
      this.selectedGatewayPaymentProfile.CcPaymentProfileId = '';
    }
    this.copySelectedCardOnFile();
  }

  public onGatewayPaymentProfileChange = (data: any): void => {
    const selected = isSavedCardSelected(data)
      ? this.gatewayPaymentProfiles?.find((x) => String(x.CcPaymentProfileId) === String(data))
      : undefined;
    this.selectedGatewayPaymentProfile = selected ?? new GatewayPaymentProfile();
    this.copySelectedCardOnFile();
  };

  public getValidationResult = (): ValidationResult => {
    let valid = new ValidationResult();
    valid.isValid = true;

    let evalPaymentOptionId = this.registration.Registration.PaymentOption;
    if (this.registration.Registration.AdditionalPaymentAmountForEdit > 0) {
      if (this.registration.IsEdit) {
        //if they have chosen a new payment option for the edit so use this to do the eval
        evalPaymentOptionId = this.registration.Registration.PaymentOptionForEdit;
      }
    }

    // no validation
    if (this.registration && this.registration.ModificationOptions && this.registration.ModificationOptions.DoNotModifyInvoice)
      return valid;

    let selectedItemsNeedingPayment = this.registration.Items.filter((item: RegistrationItem) => {
      return item.Quantity > 0 && item.ExtendedPrice > 0;
    });

    //PaymentOptionAsString = CASE WHEN EventSignUps.PaymentOption = 1 THEN 'Bill Later' WHEN EventSignUps.PaymentOption = 2 THEN 'Pay At Door' WHEN EventSignUps.PaymentOption = 3 THEN 'Credit Card' WHEN EventSignUps.PaymentOption = 4 THEN 'Purchase Order'ELSE '' END
    if (this.registration.Registration.AdditionalPaymentAmountForEdit > 0) {
      if (
        selectedItemsNeedingPayment.length > 0 &&
        evalPaymentOptionId == 3 &&
        !isSavedCardSelected(this.selectedGatewayPaymentProfile?.CcPaymentProfileId)
      ) {
        //credit card payment option

        valid = this.creditCardInputComponent.getValidationResult();
      }
      if (valid.isValid == false) {
        //error set above
      } else if (evalPaymentOptionId == 1 || evalPaymentOptionId == 2) {
        //pay at door or bill later options
        valid.isValid = true;
      }
      //else if (this._utilService.isNullOrLessThanOrEqualToZero(evalPaymentOptionId)) {
      //    //purchase order payment option
      //    valid.isValid = false;
      //    valid.errors.push(this._translationService.getInstant("events.registration.wizard.payment-info.payment-option-payment-info-needed-validation-error"));

      //}
    }

    //if (this.registration.IsEdit && this.registration.EditOriginalItemsChanged && this.registration.ModificationOptions.CreateUnappliedPayments == true && this.registration.ModificationOptions.UnappliedPaymentPaymentypeId <= 0) {
    //    //
    //    valid.isValid = false;
    //    valid.errors.push(this._translationService.getInstant("events.registration.wizard.payment-info.modification-option-unapplied-payment-type-needed-validation-error"));

    //}
    if (
      this.registration.IsEdit &&
      this.registration.EditOriginalItemsChanged &&
      this.originalAmountPaid > 0 &&
      this.registration.ModificationOptions.PaymentAdjustmentTypeId <= 0
    ) {
      //
      valid.isValid = false;
      valid.errors.push(
        this._translationService.getInstant(
          'events.registration.wizard.payment-info.modification-option-payment-adjustment-type-needed-validation-error'
        )
      );
    }

    //if (this.registration.IsEdit && this.registration.EditOriginalItemsChanged && this.registration.ModificationOptions.InvoiceAdjustmentTypeId <= 0) {
    //    //
    //    valid.isValid = false;
    //    valid.errors.push(this._translationService.getInstant("events.registration.wizard.payment-info.modification-option-invoice-adjustment-type-needed-validation-error"));

    //}

    return valid;
  };

  public onPaymentOptionChange = (data: string): void => {
    //the option comes thru as a string convert to int
    if (this.registration.IsEdit) {
      this.registration.Registration.PaymentOptionForEdit = parseInt(data);
    } else {
      this.registration.Registration.PaymentOption = parseInt(data);
    }

    this.updateRegistrationWithSelectedPaymentMethod();

    this._registrationService.calculateTotals(this.registration);

    if (this.hasCreditCardOnFileEnabled == true) {
      this.loadCardOnFileData(this.registration.Registration.ProfileId);
    }

    // if payment option is credit card, pre-populate demographic info from registration contact
    // 3 = Credit Card Payment Option
    if (
      (!this.registration.IsEdit && this.registration.Registration.PaymentOption == 3) ||
      (this.registration.IsEdit && this.registration.Registration.PaymentOptionForEdit == 3)
    ) {
      this.copyContactInfoToCCContactInfo();
    }
  };

  public updateRegistrationWithSelectedPaymentMethod = (): void => {
    // ExistingPaymentOptionFeesSet doesn't get set if there are no existing fees.
    // If the payment option gets changed (obviously after load) and payment fee has never been calculated, then act like it has.
    if (
      this.registration.Registration.ExistingPaymentOptionFeesSet == false &&
      this.registration.Registration.PaymentOptionForEdit > 0 &&
      this.registration.Registration.PaymentOption != this.registration.Registration.PaymentOptionForEdit
    )
      this.registration.Registration.ExistingPaymentOptionFeesSet = true;

    for (let payMethod of this.paymentMethods) {
      let evalMethodId = this.registration.Registration.PaymentOption;

      if (this.registration.IsEdit) {
        //evalMethodId = this.registration.Registration.PaymentOptionForEdit;
        //if (this.registration.Registration.PaymentOptionForEdit > 0) {
        //    //if they have chosen a new payment option for the edit so use this to do the eval
        //    evalMethodId = this.registration.Registration.PaymentOptionForEdit;
        //}

        if (this.registration.Registration.ExistingPaymentOptionFeesSet == true) {
          evalMethodId = this.registration.Registration.PaymentOptionForEdit;
        }
      }

      if (payMethod.PaymentMethodId == evalMethodId) {
        this.registration.Registration.SelectedPaymentMethod = payMethod;
      }
    }
  };

  public applyPromoCode = (): void => {
    this.showPromoCodeNotStarted = false;
    this.showPromoCodeExpired = false;
    this.showPromoCodeMaxedOut = false;

    if (
      this.selectedPromoCode &&
      !this.registration.PromoCodes.find((p) => {
        return p.PromoCodeId == this.selectedPromoCode;
      })
    ) {
      let promoCode = this.eventPromoCodes.find((p) => {
        return p.PromoCodeId == this.selectedPromoCode;
      });

      let endDate = new Date(promoCode.EndDate.getFullYear(), promoCode.EndDate.getMonth(), promoCode.EndDate.getDate(), 0, 0, 0);

      let today = new Date();
      today = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);

      const startDate = new Date(promoCode.StartDate.getFullYear(), promoCode.StartDate.getMonth(), promoCode.StartDate.getDate(), 0, 0, 0);

      if (promoCode.MaxUses > 0 && promoCode.Used >= promoCode.MaxUses) {
        this.showPromoCodeMaxedOut = true;
      } else if (startDate > today) {
        this.showPromoCodeNotStarted = true;
      } else if (endDate < today) {
        this.showPromoCodeExpired = true;
      } else {
        this.registration.PromoCodes.push(promoCode);

        this.selectedPromoCode = 0;
        this.getItemTotals();
      }
    }
  };

  public hidePromoCode = (promoCodeId: number, eventItemIds: number[]): boolean => {
    return (
      this.registration.PromoCodes.some((p) => p.PromoCodeId == promoCodeId) ||
      (eventItemIds.length > 0 &&
        // hide if event items for this promo code haven't been selected
        !eventItemIds.some((e) => this.registration.Items.some((ri) => ri.EventItemId == e && ri.Quantity > 0)))
    );
  };

  public showPromoCodes = (): boolean => {
    return (
      this.eventPromoCodes.some((p) => p.EventItemIds.length == 0) ||
      this.registration.Items.some(
        (i) => i.Quantity > 0 && this.eventPromoCodes.some((pc) => pc.EventItemIds.some((eid) => eid == i.EventItemId))
      )
    );
  };

  public removePromoCode = (promoCodeId: number): void => {
    let index = this.registration.PromoCodes.findIndex((p) => p.PromoCodeId == promoCodeId);
    if (index > -1) this.registration.PromoCodes.splice(index, 1);
    this.getItemTotals();
  };

  public getItemTotals = (): void => {
    this._registrationService.calculateTotals(this.registration);
  };

  public quantityChanged = (): void => {
    this.getItemTotals();
  };

  public onDoNotAddPaymentOptionFeeChange = (evt: any = null): void => {
    this._registrationService.calculateTotals(this.registration);
  };

  /**
   * copy address from regiration contact to cc contatct info
   */
  public copyContactInfoToCCContactInfo(): void {
    if (!this.registration.Registration.CreditCardPayment) this.registration.Registration.CreditCardPayment = new CreditCardPaymentInfo();
    if (!this.registration.Registration.CreditCardPayment.CreditCardContactInfo)
      this.registration.Registration.CreditCardPayment.CreditCardContactInfo = new CreditCardContactInfo();

    // copy fields
    let nameOnCard = this.registration.Registration.FName;

    if (this.registration.Registration.LName) {
      if (nameOnCard) nameOnCard += ' ' + this.registration.Registration.LName;
      else nameOnCard = ' ' + this.registration.Registration.LName;
    }

    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.NameOnCard = nameOnCard;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.FirstName = this.registration.Registration.FName;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.LastName = this.registration.Registration.LName;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.OrgName = this.registration.Registration.OrgName;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.Address1 = this.registration.Registration.Address1;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.Address2 = this.registration.Registration.Address2;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.City = this.registration.Registration.City;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.State = this.registration.Registration.State;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.Zip = this.registration.Registration.Zip;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.ZipExt = this.registration.Registration.ZipExt;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.Country = this.registration.Registration.Country;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.Phone = this.registration.Registration.Phone;
    this.registration.Registration.CreditCardPayment.CreditCardContactInfo.Email = this.registration.Registration.Email;
  }

  public copySelectedCardOnFile(): void {
    if (!this.registration.Registration.GatewayPaymentProfile) {
      this.registration.Registration.GatewayPaymentProfile = new GatewayPaymentProfile();
    }

    this.registration.Registration.GatewayPaymentProfile = this.selectedGatewayPaymentProfile;

    //console.error(this.registration.Registration.SelectedGatewayPaymentProfile);
    //console.error(this.selectedGatewayPaymentProfile);
  }

  public refreshBenefits = (): void => {
    this.hasBenefitsThatCanBeUsed = false;
    this.availableEventBenefits = new Array<EventBenefit>();
    this.availableProfileBenefits = new Array<ProfileBenefit>();
    this.availableBenefitsToUse = new Array<EventBenefit>();

    if (this.registration.Registration.ProfileId > 0) {
      let p1 = this._benefitService.getEventBenefits(this.event.EventId).then((data: Array<EventBenefit>) => {
        let selectedItemIds = this.registration.Items.filter((x) => x.Quantity > 0).map((x) => x.EventItemId);
        this.availableEventBenefits = data.filter((eventBenefit) => {
          // "Selected" property is not used in this case.
          return eventBenefit.EventItems.some((ebItem) => selectedItemIds.indexOf(ebItem.EventPriceId) > -1);
        });
      });

      let p2 = this._benefitService
        .getProfileEventBenefits(this.registration.Registration.ProfileId, this.event.EventId)
        .then((data: Array<ProfileBenefit>) => {
          this.availableProfileBenefits = data;
        });

      Promise.all([p1, p2]).then(() => {
        //use only benefits allowed for the vent that the profile also has and has not yet used up
        if (this.availableEventBenefits.length > 0 && this.availableProfileBenefits.length > 0) {
          this.availableBenefitsToUse = new Array<EventBenefit>();

          for (let profileBenefit of this.availableProfileBenefits) {
            if (profileBenefit.QuantityRemaining > 0 || profileBenefit.ValueRemaining > 0) {
              for (let eventBenefit of this.availableEventBenefits) {
                if (eventBenefit.BenefitTypeId == profileBenefit.BenefitTypeId) {
                  this.hasBenefitsThatCanBeUsed = true;
                  this.availableBenefitsToUse.push(eventBenefit);
                }
              }
            }
          }
        }

        //if this had previous benefits applied immediately set them to current benefits being used
        //as our ui will deal with only benefits we think we are going use going forward as old ones are cleared out on edit
        if (
          this.registration.Registration.BenefitsPreviouslyAppliedTotal > 0 &&
          this.registration.Registration.BenefitsPreviouslyApplied.length > 0
        ) {
          // original implimentation supported a single benefit at a time. keeping it that way for now.
          let previousEventBenefitId = this.registration.Registration.BenefitsPreviouslyApplied[0].EventBenefitId;
          let previousBenefitsQuantity = this.registration.Registration.BenefitsPreviouslyApplied.reduce(
            (sum, b) => sum + b.QuantityToApply,
            0
          );
          let previousBenefitsValue = this.registration.Registration.BenefitsPreviouslyApplied.reduce((sum, b) => sum + b.ValueToApply, 0);
          //for (let ben of this.registration.Registration.BenefitsPreviouslyApplied) {
          //    previousBenefitsQuantity = previousBenefitsQuantity + ben.QuantityToApply;
          //    previousBenefitsValue = previousBenefitsValue + ben.ValueToApply;
          //}

          this.registration.Registration.BenefitsToApply = this.registration.Registration.BenefitsPreviouslyApplied;
          this.registration.Registration.BenefitsToApplyTotal = this.registration.Registration.BenefitsPreviouslyAppliedTotal;

          //this.registration.Registration.BenefitsPreviouslyApplied = new Array<RegistrationProfileBenefit>();
          //this.registration.Registration.BenefitsPreviouslyAppliedTotal = 0;

          //select the benefit from the list as if a user had - comes thru as string value
          this.onBenefitChange(previousEventBenefitId.toString());

          //set the amounts/quantity after selecting the item in the dropdown
          this.selectedRegistrationBenefit.ValueToApply = previousBenefitsValue;
          this.selectedRegistrationBenefit.QuantityToApply = previousBenefitsQuantity;
        }
      });
    } else {
      this.availableEventBenefits = new Array<EventBenefit>();
      this.availableProfileBenefits = new Array<ProfileBenefit>();
    }
  };

  public onBenefitChange = (data: string) => {
    this.selectedRegistrationBenefit = new RegistrationProfileBenefit();

    let selectedEventBenefitId = parseInt(data);
    this.selectedEventBenefit.EventBenefitId = selectedEventBenefitId;

    let eventBenefitResult = this.availableBenefitsToUse.find((x: EventBenefit) => {
      return x.EventBenefitId == selectedEventBenefitId;
    });
    if (this._utilService.isNotNullOrUndefined(eventBenefitResult)) {
      this._utilService.mapSafe(eventBenefitResult, this.selectedEventBenefit);
      this.selectedEventBenefit.EventItems = [];
      for (let it of eventBenefitResult.EventItems) {
        let epb = new EventPriceBenefit();
        this._utilService.mapSafe(it, epb);
        this.selectedEventBenefit.EventItems.push(epb);
      }

      let profileBenefitResult = this.availableProfileBenefits.find((x: ProfileBenefit) => {
        return x.BenefitTypeId == eventBenefitResult.BenefitTypeId;
      });
      if (this._utilService.isNotNullOrUndefined(profileBenefitResult)) {
        this._utilService.mapSafe(profileBenefitResult, this.selectedProfileBenefit);

        this.selectedRegistrationBenefit = new RegistrationProfileBenefit();
        this.selectedRegistrationBenefit.BenefitType = this.selectedEventBenefit.BenefitType;
        this.selectedRegistrationBenefit.BenefitTypeId = this.selectedProfileBenefit.BenefitTypeId;
        this.selectedRegistrationBenefit.EventBenefitId = this.selectedEventBenefit.EventBenefitId;
        this.selectedRegistrationBenefit.ProfileBenefitId = this.selectedProfileBenefit.Id;
        this.selectedRegistrationBenefit.ProfileId = this.selectedProfileBenefit.ProfileId;

        this.selectedRegistrationBenefit.LimitType = this.selectedEventBenefit.LimitType;
        this.selectedRegistrationBenefit.LimitAmount = this.selectedEventBenefit.LimitAmount;
        this.updateBenefitSuggestedAmountToUse();
        //this.applyBenefit();
      }
    }
  };
  public updateBenefitSuggestedAmountToUse = (): void => {
    let maxAllowed = this.getMaxBenefitAmountAllowed();

    this.selectedRegistrationBenefit.ValueToApply = maxAllowed;
  };

  public getMaxAmtOfBenefitsThatCanBeUsedForItems = (): number => {
    //per lauren base on items totals not payment option fees and taxes
    let max = 0;
    for (let itemTotal of this.registration.Registration.EventItemTotals) {
      max = max + itemTotal.Total;
    }
    return max;
  };

  public getMaxBenefitAmountAllowed = (): number => {
    let max = this.getMaxAmtOfBenefitsThatCanBeUsedForItems();

    if (this.selectedProfileBenefit.ValueRemaining < max) {
      max = this.selectedProfileBenefit.ValueRemaining;
    }
    return max;
  };
  public getMaxBenefitQuantityAllowed = (): number => {
    let max = this.getMaxAmtOfBenefitsThatCanBeUsedForItems();

    if (this.selectedProfileBenefit.QuantityRemaining < max) {
      max = this.selectedProfileBenefit.QuantityRemaining;
    }
    return max;
  };

  public applyBenefit = (): void => {
    this.registration.Registration.BenefitsToApply = new Array<RegistrationProfileBenefit>();
    if (this.selectedRegistrationBenefit.BenefitTypeId > 0) {
      let maxAllowed: number = 0;
      let maxAmount: number = 0;
      // the totals amount of all items. if this amount is somehow exceeded, we fix it.
      let itemsTotal = this.getMaxAmtOfBenefitsThatCanBeUsedForItems();
      let remainingQuantityToApply: number;
      let remainingAmountToApply: number;
      if (this.selectedEventBenefit.LimitType == 1) {
        maxAllowed = this.selectedProfileBenefit.QuantityRemaining;
        remainingQuantityToApply = this.selectedRegistrationBenefit.QuantityToApply;
        if (remainingQuantityToApply > maxAllowed) remainingQuantityToApply = maxAllowed;
      } else {
        maxAmount = this.getMaxBenefitAmountAllowed();
        remainingAmountToApply = this.selectedRegistrationBenefit.ValueToApply;
        if (remainingAmountToApply > maxAmount) remainingAmountToApply = maxAmount;
      }
      let totalApplied: number = 0;
      for (let selectedItem of this.registration.Items) {
        if (selectedItem.Quantity > 0 && (remainingQuantityToApply > 0 || remainingAmountToApply > 0) && totalApplied < itemsTotal) {
          let macthingEventBenefit = this.selectedEventBenefit.EventItems.find((x) => x.EventPriceId == selectedItem.EventItemId);
          if (this._utilService.isNotNullOrUndefined(macthingEventBenefit)) {
            let benefitPerItemAmt = this._utilService.round(selectedItem.ExtendedPrice / selectedItem.Quantity, 2);
            let newRegistrationBenefit: RegistrationProfileBenefit = new RegistrationProfileBenefit();
            this._utilService.mapSafe(this.selectedRegistrationBenefit, newRegistrationBenefit);
            newRegistrationBenefit.EventPriceId = selectedItem.EventItemId;
            if (newRegistrationBenefit.LimitType == 1) {
              newRegistrationBenefit.QuantityToApply = Math.min(selectedItem.Quantity, remainingQuantityToApply);
              newRegistrationBenefit.ValueToApply = benefitPerItemAmt * newRegistrationBenefit.QuantityToApply;
              remainingQuantityToApply -= newRegistrationBenefit.QuantityToApply;
            } else {
              newRegistrationBenefit.QuantityToApply = 0;
              newRegistrationBenefit.ValueToApply = Math.min(benefitPerItemAmt * selectedItem.Quantity, remainingAmountToApply);
              remainingAmountToApply -= remainingAmountToApply;
            }
            totalApplied += newRegistrationBenefit.ValueToApply;

            // if the total benefits somehow exceed the items total, we fix it here.
            if (totalApplied > itemsTotal) {
              newRegistrationBenefit.ValueToApply = Math.max(0, newRegistrationBenefit.ValueToApply - (itemsTotal - totalApplied));
            }

            this.registration.Registration.BenefitsToApply.push(newRegistrationBenefit);
          }
        }
      }
    }
    //if (this.selectedRegistrationBenefit.BenefitTypeId > 0) {
    //    // clear the benefits applied to since we're recalculating everything anyway
    //    this.selectedRegistrationBenefit.EventItemsAppliedTo = new Array<RegistrationEventItemBenefit>();
    //    //ensure is less that allowed
    //    let maxAllowed = 0;
    //    if (this.selectedEventBenefit.LimitType == 1) {
    //        maxAllowed = this.getMaxBenefitQuantityAllowed();
    //        this.selectedRegistrationBenefit.ValueToApply = 0;
    //        if (this.selectedRegistrationBenefit.QuantityToApply > maxAllowed) {
    //            this.selectedRegistrationBenefit.QuantityToApply = maxAllowed
    //        }
    //        //this is quantity so back into what the $ value is
    //        let benefitPerItemAmt = 0;
    //        let totalToApply = 0;
    //        let remainingQuantityToApply = this.selectedRegistrationBenefit.QuantityToApply;
    //        for (let selectedItem of this.registration.Items) {
    //            if (selectedItem.Quantity > 0 && remainingQuantityToApply > 0) {
    //                let macthingEventBenefit = this.selectedEventBenefit.EventItems.find(x => x.EventPriceId == selectedItem.EventItemId);
    //                if (this._utilService.isNotNullOrUndefined(macthingEventBenefit)) {
    //                    benefitPerItemAmt = this._utilService.round(selectedItem.ExtendedPrice / selectedItem.Quantity, 2);
    //                    let eventItemBenefit = new RegistrationEventItemBenefit();
    //                    eventItemBenefit.EventItemId = selectedItem.EventItemId;
    //                    eventItemBenefit.QuantityApplied = Math.min(selectedItem.Quantity, remainingQuantityToApply)
    //                    eventItemBenefit.ValueApplied = benefitPerItemAmt * eventItemBenefit.QuantityApplied;
    //                    remainingQuantityToApply -= eventItemBenefit.QuantityApplied;
    //                    totalToApply += eventItemBenefit.ValueApplied;
    //                    this.selectedRegistrationBenefit.EventItemsAppliedTo.push(eventItemBenefit);
    //                }

    //            }
    //        }
    //        this.selectedRegistrationBenefit.ValueToApply = totalToApply;
    //        let max = this.getMaxAmtOfBenefitsThatCanBeUsedForItems();
    //        if (max < this.selectedRegistrationBenefit.ValueToApply) {
    //            this.selectedRegistrationBenefit.ValueToApply = max;
    //        }

    //    } else {
    //        maxAllowed = this.getMaxBenefitAmountAllowed();
    //        this.selectedRegistrationBenefit.QuantityToApply = 0;
    //        if (this.selectedRegistrationBenefit.ValueToApply > maxAllowed) {
    //            this.selectedRegistrationBenefit.ValueToApply = maxAllowed
    //        }
    //    }

    //    this.registration.Registration.BenefitsToApply.push(this.selectedRegistrationBenefit);
    //}

    this._registrationService.calculateTotals(this.registration);
  };

  public onBenefitAmountToApplyChange = (): void => {
    //this.applyBenefit();
  };

  public onBenefitQuantityToApplyChange = (value: string): void => {
    let numValue = parseInt(value.toString());
    value = numValue.toString();
    this.selectedRegistrationBenefit.QuantityToApply = numValue;
    //this.applyBenefit();
  };

  public removeBenefits() {
    this.registration.Registration.BenefitsToApply = new Array<RegistrationProfileBenefit>();
  }
}
